import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Users, Clock, MapPin, MessageSquare, User, ThumbsUp } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import DashboardHeader from '../components/DashboardHeader';
import NavigationCarousel from '../components/NavigationCarousel';
import SearchBar from '../components/SearchBar';

const Community = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('discussions');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDiscussion, setSelectedDiscussion] = useState(null);
  const [showDiscussionModal, setShowDiscussionModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [showCourseNotification, setShowCourseNotification] = useState(false);
  const [courseNotificationMessage, setCourseNotificationMessage] = useState('');
  const [showNewDiscussionModal, setShowNewDiscussionModal] = useState(false);

  const handleSearch = (searchTerm) => {
    console.log('Search Community:', searchTerm);
    setSearchTerm(searchTerm);
  };

  // Comprehensive filter function that searches across all fields
  const filterContent = (contentArray, type = 'discussion') => {
    if (!searchTerm) return contentArray;
    const lowerSearch = searchTerm.toLowerCase();
    return contentArray.filter(item => {
      // Search in title/name
      const titleMatch = item.title?.toLowerCase().includes(lowerSearch) || item.name?.toLowerCase().includes(lowerSearch) || item.courseName?.toLowerCase().includes(lowerSearch);
      // Search in description/content
      const descMatch = item.description?.toLowerCase().includes(lowerSearch) || item.content?.toLowerCase().includes(lowerSearch);
      // Search in author/instructor
      const authorMatch = item.author?.toLowerCase().includes(lowerSearch) || item.instructor?.toLowerCase().includes(lowerSearch) || item.instructorArabic?.includes(searchTerm) || item.instructorEnglish?.toLowerCase().includes(lowerSearch);
      // Search in category
      const categoryMatch = item.category?.toLowerCase().includes(lowerSearch);
      // Search in course
      const courseMatch = item.course?.toLowerCase().includes(lowerSearch);
      
      return titleMatch || descMatch || authorMatch || categoryMatch || courseMatch;
    });
  };

  const handleDiscussionClick = (discussion) => {
    setSelectedDiscussion(discussion);
    setShowDiscussionModal(true);
  };

  const handleGroupClick = (group) => {
    setSelectedGroup(group);
    setShowGroupModal(true);
  };

  const handleJoinGroup = (groupName) => {
    setNotificationMessage(`Joined ${groupName}!\n\nYou'll receive notifications about upcoming sessions and can access group materials.`);
    setShowNotification(true);
    setShowGroupModal(false);
    
    // Auto-hide notification after 4 seconds
    setTimeout(() => {
      setShowNotification(false);
    }, 4000);
  };

  const handleNewDiscussion = () => {
    setShowNewDiscussionModal(true);
  };

  const handleJoinCourseDiscussion = (courseName) => {
    setCourseNotificationMessage(`Successfully joined ${courseName} discussion!\n\nYou can now:\n• View all discussion topics\n• Reply to existing conversations\n• Ask questions to peers and instructors\n• Get notifications about new posts`);
    setShowCourseNotification(true);
    
    // Auto-hide notification after 4 seconds
    setTimeout(() => {
      setShowCourseNotification(false);
    }, 4000);
  };

  const tabs = [
    { id: 'discussions', label: 'DISCUSSIONS' },
    { id: 'courses', label: 'COURSES' },
    { id: 'study-groups', label: 'STUDY GROUPS' },
  ];

  const discussions = [
    {
      id: 1,
      title: 'Struggling with BIM Assignment - Need help with Revit families',
      author: 'Alex Chen',
      authorRole: '3rd Year Student',
      authorAvatar: 'AC',
      replies: 18,
      lastActive: '1 hour ago',
      category: 'Course Help',
      isHot: true,
      upvotes: 23,
      views: 145,
      course: 'BIM APPLI.: GIS MAPPING',
      credits: 0,
      content: `Hey everyone! I'm working on the Revit families assignment for BIM class and I'm completely stuck. The professor wants us to create parametric families but I can't figure out how to set up the constraints properly.

**Specific issues:**
• Door family won't resize correctly
• Window parameters aren't working
• Can't get the visibility graphics right
• Family types keep breaking

**Assignment due:** Next Friday
**Professor:** Dr. Martinez

Anyone who's done this before or is currently working on it? I could really use some guidance! I have the tutorial videos but they're not helping with the specific problems I'm facing.`,
      conversation: [
        {
          author: 'Sarah Kim',
          authorRole: '4th Year Student',
          authorAvatar: 'SK',
          timestamp: '45 minutes ago',
          content: `I just finished this assignment! The key is to set up your reference planes first before adding any geometry. Make sure they're locked and constrained properly. Also, check that your parameters are set to "Instance" vs "Type" - that's usually where people mess up.`,
          upvotes: 8,
          replies: [
            {
              author: 'Alex Chen',
              authorRole: '3rd Year Student',
              authorAvatar: 'AC',
              timestamp: '30 minutes ago',
              content: `Thanks Sarah! That's super helpful. Do you have any screenshots of your reference plane setup? I think I might be doing that wrong.`,
              upvotes: 2
            }
          ]
        },
        {
          author: 'Prof. Martinez',
          authorRole: 'Professor',
          authorAvatar: 'PM',
          timestamp: '30 minutes ago',
          content: `Great question Alex! I'll be holding extra office hours tomorrow from 2-4 PM specifically for this assignment. Also, check the course materials - I uploaded a step-by-step guide last week. The most common mistake is not properly constraining the geometry to the reference planes.`,
          upvotes: 15,
          replies: []
        },
        {
          author: 'Mike Rodriguez',
          authorRole: '3rd Year Student',
          authorAvatar: 'MR',
          timestamp: '20 minutes ago',
          content: `I'm working on this too! Want to study together? I'm free tomorrow afternoon. We could work through it step by step.`,
          upvotes: 5,
          replies: []
        }
      ]
    },
    {
      id: 2,
      title: 'Study materials for Spatial Reasoning midterm - what to focus on?',
      author: 'Emily Johnson',
      authorRole: '2nd Year Student',
      authorAvatar: 'EJ',
      replies: 24,
      lastActive: '2 hours ago',
      category: 'Study Materials',
      isHot: true,
      upvotes: 31,
      views: 189,
      course: 'INTRO TO SPATIAL REASONING',
      credits: 0,
      content: `The midterm is next week and I'm feeling overwhelmed! There's so much material to cover and I'm not sure what to prioritize.

**Topics covered so far:**
• 3D visualization techniques
• Orthographic projections
• Isometric drawings
• Spatial relationships
• Mental rotation tasks

**What I'm struggling with:**
• Complex isometric projections
• Mental rotation under time pressure
• Converting between different view types

**Study resources I have:**
• Textbook chapters 1-6
• Practice problems from Canvas
• Old exam samples

What should I focus on? Any study tips from people who already took this class?`,
      conversation: [
        {
          author: 'David Park',
          authorRole: '3rd Year Student',
          authorAvatar: 'DP',
          timestamp: '1 hour ago',
          content: `I took this class last semester! The key is practicing mental rotation daily - even 15 minutes helps. The professor loves to test on orthographic to isometric conversion, so definitely focus on that. Also, the old exam samples are gold - they're very similar to the actual test.`,
          upvotes: 12,
          replies: []
        },
        {
          author: 'Lisa Chen',
          authorRole: '2nd Year Student',
          authorAvatar: 'LC',
          timestamp: '45 minutes ago',
          content: `I'm in the same boat! Want to form a study group? We could meet at the library and work through practice problems together. I'm free most evenings this week.`,
          upvotes: 8,
          replies: []
        },
        {
          author: 'Prof. Wilson',
          authorRole: 'Professor',
          authorAvatar: 'PW',
          timestamp: '30 minutes ago',
          content: `Great question Emily! Focus on the practice problems I assigned - they're very similar to the exam format. The mental rotation section is worth 30% of the grade, so definitely practice that. I'll be in my office today from 3-5 PM if anyone wants to review.`,
          upvotes: 18,
          replies: []
        }
      ]
    },
    {
      id: 3,
      title: 'Sustainability Design Project - Group looking for 4th member',
      author: 'Marcus Rodriguez',
      authorRole: '4th Year Student',
      authorAvatar: 'MR',
      replies: 12,
      lastActive: '3 hours ago',
      category: 'Study Groups',
      isHot: false,
      upvotes: 15,
      views: 67,
      course: 'INTRO TO SUSTAINABILITY & DESIGN',
      credits: 50,
      content: `Our group needs one more member for the sustainability design project! We're working on a LEED-certified residential complex design.

**Current group:**
• Me (4th year, focus on structural design)
• Sarah (3rd year, environmental systems)
• Alex (3rd year, landscape architecture)

**Project requirements:**
• 20-page design report
• 3D model and renderings
• LEED scorecard analysis
• Presentation to class

**What we need:**
• Someone with strong Revit skills
• Available for weekly meetings (Tuesdays 7 PM)
• Can commit 8-10 hours per week

**Credits:** 50 XP for joining (professor-assigned group)
**Due date:** March 15th

Interested? DM me or reply here!`,
      conversation: [
        {
          author: 'Jennifer Liu',
          authorRole: '3rd Year Student',
          authorAvatar: 'JL',
          timestamp: '2 hours ago',
          content: `I'm interested! I have strong Revit skills and I'm taking the same class. I can definitely commit to the time requirements. When can we meet to discuss the project details?`,
          upvotes: 6,
          replies: []
        },
        {
          author: 'Marcus Rodriguez',
          authorRole: '4th Year Student',
          authorAvatar: 'MR',
          timestamp: '1 hour ago',
          content: `Perfect Jennifer! Let's meet tomorrow at 7 PM in the architecture studio. I'll send you the project brief and our current progress.`,
          upvotes: 3,
          replies: []
        }
      ]
    },
    {
      id: 4,
      title: 'Structures I-III - Need help with moment diagrams',
      author: 'Amanda Foster',
      authorRole: '3rd Year Student',
      authorAvatar: 'AF',
      replies: 16,
      lastActive: '4 hours ago',
      category: 'Course Help',
      isHot: false,
      upvotes: 19,
      views: 98,
      course: 'STRUCTURES I-III',
      credits: 0,
      content: `I'm completely lost on moment diagrams for beams! The homework is due tomorrow and I can't figure out how to draw them correctly.

**Problem:** Simply supported beam with point load at center
**Length:** 8m
**Load:** 20 kN at 4m from left support

**What I'm getting wrong:**
• Maximum moment calculation
• Shape of the diagram
• Sign conventions
• Shear force relationship

**Resources I've tried:**
• Textbook examples
• YouTube tutorials
• Office hours (missed them)

Anyone available to help? I can share my work so far and we can go through it step by step.`,
      conversation: [
        {
          author: 'Carlos Mendez',
          authorRole: '4th Year Student',
          authorAvatar: 'CM',
          timestamp: '3 hours ago',
          content: `I can help! For a simply supported beam with center point load, the max moment is PL/4. So for your case: (20 kN × 8m)/4 = 40 kN⋅m. The diagram is triangular with max at center. Want to meet at the library?`,
          upvotes: 7,
          replies: []
        },
        {
          author: 'Prof. Thompson',
          authorRole: 'Professor',
          authorAvatar: 'PT',
          timestamp: '2 hours ago',
          content: `Amanda, I'll be in my office until 6 PM today. Come by and I'll walk you through the moment diagram step by step. Also, check the course website - I posted a detailed solution guide yesterday.`,
          upvotes: 12,
          replies: []
        }
      ]
    }
  ];

  const [discussionUpvotes, setDiscussionUpvotes] = useState(
    discussions.map((discussion) => discussion.upvotes)
  );

  const handleDiscussionUpvote = (event, index) => {
    event.stopPropagation();
    setDiscussionUpvotes((prev) => {
      const next = [...prev];
      next[index] = next[index] + 1;
      return next;
    });
  };

  const studyGroups = [
    {
      id: 1,
      name: 'BIM Study Group - Prof. Martinez',
      members: 12,
      maxMembers: 15,
      description: 'Professor-assigned study group for BIM Applications course. Focus on Revit families, parametric modeling, and GIS integration.',
      nextSession: 'Tomorrow 2:00 PM',
      category: 'Professor-Assigned',
      course: 'BIM APPLI.: GIS MAPPING',
      credits: 75,
      professor: 'Dr. Martinez',
      requirements: 'Must be enrolled in BIM course',
      isProfessorAssigned: true,
      meetingLocation: 'Architecture Studio 201',
      weeklyHours: 6
    },
    {
      id: 2,
      name: 'Spatial Reasoning Prep Group',
      members: 8,
      maxMembers: 10,
      description: 'Student-led study group for Spatial Reasoning midterm prep. Practice problems, study sessions, and peer tutoring.',
      nextSession: 'Today 7:00 PM',
      category: 'Student-Led',
      course: 'INTRO TO SPATIAL REASONING',
      credits: 25,
      professor: 'Prof. Wilson',
      requirements: 'Open to all students',
      isProfessorAssigned: false,
      meetingLocation: 'Library Study Room 3',
      weeklyHours: 4
    },
    {
      id: 3,
      name: 'Sustainability Design Project Team',
      members: 3,
      maxMembers: 4,
      description: 'Professor-assigned group project for LEED-certified residential complex design. Multi-disciplinary team collaboration.',
      nextSession: 'Tuesday 7:00 PM',
      category: 'Professor-Assigned',
      course: 'INTRO TO SUSTAINABILITY & DESIGN',
      credits: 100,
      professor: 'Dr. Chen',
      requirements: 'Must be enrolled in Sustainability course',
      isProfessorAssigned: true,
      meetingLocation: 'Architecture Studio 205',
      weeklyHours: 8
    },
    {
      id: 4,
      name: 'Structures Study Circle',
      members: 15,
      maxMembers: 20,
      description: 'Student-led study group for Structures I-III. Focus on moment diagrams, beam analysis, and structural calculations.',
      nextSession: 'Wednesday 6:00 PM',
      category: 'Student-Led',
      course: 'STRUCTURES I-III',
      credits: 30,
      professor: 'Prof. Thompson',
      requirements: 'Open to all students',
      isProfessorAssigned: false,
      meetingLocation: 'Engineering Building Room 101',
      weeklyHours: 5
    },
    {
      id: 5,
      name: 'VR Design Workshop - Prof. Johnson',
      members: 6,
      maxMembers: 8,
      description: 'Professor-led workshop on VR tools for architectural visualization. Hands-on training with Enscape, Twinmotion, and Unreal Engine.',
      nextSession: 'Friday 3:00 PM',
      category: 'Professor-Assigned',
      course: 'DESIGN TECHNOLOGY',
      credits: 60,
      professor: 'Prof. Johnson',
      requirements: 'Must have completed BIM course',
      isProfessorAssigned: true,
      meetingLocation: 'VR Lab - Architecture Building',
      weeklyHours: 6
    },
    {
      id: 6,
      name: 'Geotechnical Engineering Study Group',
      members: 10,
      maxMembers: 12,
      description: 'Student-led study group for Foundations of Geotechnical Engineering. Soil analysis, foundation design, and lab report collaboration.',
      nextSession: 'Thursday 5:00 PM',
      category: 'Student-Led',
      course: 'FOUNDATIONS OF GEOTECHNICAL ENGINEE-',
      credits: 35,
      professor: 'Dr. Park',
      requirements: 'Open to all students',
      isProfessorAssigned: false,
      meetingLocation: 'Civil Engineering Lab',
      weeklyHours: 4
    }
  ];

  const courseDiscussions = [
    {
      courseName: 'Introduction to Sustainability',
      instructor: 'Prof. Ahmed Al-Hassan / Dr. Sarah Wilson',
      instructorArabic: 'أحمد الحسن',
      instructorEnglish: 'Dr. Sarah Wilson',
      activeTopics: 8,
      participants: 67,
      lastPost: '30 minutes ago',
      lastReplyBy: 'Mariam K: "Thanks for the clarification on LEED certification requirements!"',
      credits: 3,
      dueDate: '2024-03-15',
      hasDueDate: true
    },
    {
      courseName: 'Spatial Reasoning Fundamentals',
      instructor: 'Dr. Omar Al-Rashid / Prof. Michael Thompson',
      instructorArabic: 'عمر الراشد',
      instructorEnglish: 'Prof. Michael Thompson',
      activeTopics: 12,
      participants: 54,
      lastPost: '1 hour ago',
      lastReplyBy: 'Ahmed M: "The 3D visualization exercises are really challenging but helpful"',
      credits: 4,
      dueDate: null,
      hasDueDate: false
    },
    {
      courseName: 'BIM Applications: GIS Mapping',
      instructor: 'Prof. Fatima Al-Zahra / Dr. James Chen',
      instructorArabic: 'فاطمة الزهراء',
      instructorEnglish: 'Dr. James Chen',
      activeTopics: 6,
      participants: 38,
      lastPost: '3 hours ago',
      lastReplyBy: 'Youssef A: "Can someone help with the parametric family assignment?"',
      credits: 5,
      dueDate: '2024-03-20',
      hasDueDate: true
    }
  ];

  // All content in one object for unified search results
  const allContent = {
    discussions: discussions,
    studyGroups: studyGroups,
    courses: courseDiscussions
  };

  const handleNavigationClick = (item) => {
    if (item.isLogout) {
      navigate(item.path);
    } else {
      navigate(item.path);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const menuItems = [
    { label: 'HOME', path: '/app' },
    { label: 'STUDY', path: '/app/study' },
    { label: 'CHALLENGES', path: '/app/challenges' },
    { label: 'COMMUNITY', path: '/app/community' },
    { label: 'PIN UP', path: '/app/pinup' },
    { label: 'PROGRESS', path: '/app/progress' },
    { label: 'PROFILE', path: '/app/profile' },
    { label: 'LOG OUT', path: '/login', isLogout: true }
  ];

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Sidebar Navigation */}
      <div className="fixed top-0 left-0 h-screen z-40">
        <NavigationCarousel
          menuItems={menuItems}
          onItemClick={handleNavigationClick}
          isCollapsed={isSidebarCollapsed}
          onToggleSidebar={toggleSidebar}
        />
      </div>

      {/* Main Content - Adjusted for Sidebar */}
      <div className={`transition-all duration-300 ${isSidebarCollapsed ? 'ml-20' : 'ml-56'}`}>
        {/* Custom Header */}
        <DashboardHeader title="COMMUNITY" isSidebarCollapsed={isSidebarCollapsed} />

        {/* Search Bar Section */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
          <div className="max-w-7xl px-6 py-4" style={{ 
            marginLeft: '20px',
            transition: 'margin-left 0.3s ease'
          }}>
            <SearchBar 
              placeholder="Search discussions, groups, courses..." 
              onSearch={handleSearch}
            />
          </div>
        </div>

        {/* Tab Navigation */}
        <div className={`${isDarkMode ? 'bg-gray-900' : 'bg-white'} border-t border-gray-200`}>
          <div className="max-w-7xl px-6" style={{ 
            marginLeft: '20px',
            transition: 'margin-left 0.3s ease'
          }}>
            <div className="flex">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative flex-1 py-6 font-bold text-sm uppercase tracking-wider transition-all duration-300 ${
                    activeTab === tab.id
                      ? 'text-[#AC5757]'
                      : isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <span className="relative z-10">{tab.label}</span>
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#AC5757] rounded-t-lg"></div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Search Results Section */}
        {searchTerm && (
          <div className="max-w-7xl px-6 py-6" style={{ 
            marginLeft: '20px',
            transition: 'margin-left 0.3s ease'
          }}>
            <div className="mb-4">
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Search Results for "{searchTerm}"
              </h2>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Found {filterContent(allContent.discussions).length + filterContent(allContent.studyGroups).length + filterContent(allContent.courses).length} results
              </p>
            </div>

            {/* Discussions Results */}
            {filterContent(allContent.discussions).length > 0 && (
              <section className="mb-6">
                <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Discussions ({filterContent(allContent.discussions).length})
                </h3>
                <div className="space-y-4">
                  {filterContent(allContent.discussions).map((discussion, idx) => (
                    <div 
                      key={idx} 
                      className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow cursor-pointer`}
                      onClick={() => handleDiscussionClick(discussion)}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} hover:text-[#AC5757] transition-colors`}>
                              {discussion.title}
                            </h3>
                            {/* Hot badge removed as requested */}
                          </div>
                          <div className={`flex items-center gap-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-3`}>
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 bg-[#AC5757] rounded-full flex items-center justify-center text-white text-xs font-bold">
                                {discussion.authorAvatar}
                              </div>
                              <span>by {discussion.author}</span>
                              <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>• {discussion.authorRole}</span>
                            </div>
                            <span>•</span>
                            <span>{discussion.replies} replies</span>
                            <span>•</span>
                            <span>{discussion.lastActive}</span>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-gray-800 text-gray-200' : 'bg-gray-50 text-gray-800'}`}>
                          {discussion.category}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 mb-4 line-clamp-3">
                        {discussion.content.substring(0, 200)}...
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Study Groups Results */}
            {filterContent(allContent.studyGroups).length > 0 && (
              <section className="mb-6">
                <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Study Groups ({filterContent(allContent.studyGroups).length})
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {filterContent(allContent.studyGroups).map((group, idx) => (
                    <div 
                      key={idx} 
                      className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl bg-gradient-to-br from-[#AC5757]/5 to-[#AC5757]/10 p-6 hover:shadow-lg transition-shadow cursor-pointer`}
                      onClick={() => handleGroupClick(group)}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{group.name}</h3>
                            {group.isProfessorAssigned && (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#AC5757] text-white">
                                Professor-Assigned
                              </span>
                            )}
                          </div>
                          <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm mb-2`}>{group.description}</p>
                        </div>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#AC5757]/20 text-[#AC5757]">
                          {group.category}
                        </span>
                      </div>
                      <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-3 mb-4`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Credits Available</span>
                          <span className="text-lg font-bold text-[#AC5757]">{group.credits} XP</span>
                        </div>
                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Earn credits by participating in study sessions and completing group activities
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Course Discussions Results */}
            {filterContent(allContent.courses).length > 0 && (
              <section className="mb-6">
                <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Course Discussions ({filterContent(allContent.courses).length})
                </h3>
                <div className="space-y-4">
                  {filterContent(allContent.courses).map((course, idx) => (
                    <div key={idx} className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow cursor-pointer`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} text-lg`}>{course.courseName}</h3>
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                              {course.credits} Credits
                            </span>
                          </div>
                          <div className="mb-3">
                            <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm mb-1`}>
                              <span className="font-medium">Arabic:</span> {course.instructorArabic}
                            </p>
                            <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>
                              <span className="font-medium">English:</span> {course.instructorEnglish}
                            </p>
                          </div>
                        </div>
                        <button 
                          onClick={() => handleJoinCourseDiscussion(course.courseName)}
                          className="bg-[#AC5757] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#8A4A4A] transition-colors"
                        >
                          Join Discussion
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}

        {/* Challenge Sections */}
        {!searchTerm && (
          <div className="max-w-7xl px-6 py-8" style={{ 
            marginLeft: '20px',
            transition: 'margin-left 0.3s ease'
          }}>

        {/* Discussions Tab */}
        {activeTab === 'discussions' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className={`font-judson text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Recent Discussions</h2>
              <button 
                onClick={handleNewDiscussion}
                className="bg-[#AC5757] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#8A4A4A] transition-colors"
              >
                New Discussion
              </button>
            </div>
            
            <div className="space-y-4">
              {discussions.map((discussion, idx) => (
                <div 
                  key={idx} 
                  className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow cursor-pointer`}
                  onClick={() => handleDiscussionClick(discussion)}
                >
                  <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'} hover:text-[#AC5757] transition-colors mb-4`}>
                          {discussion.title}
                        </h3>

                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-8 h-8 bg-[#AC5757] rounded-full flex items-center justify-center text-white text-xs font-bold">
                            {discussion.authorAvatar}
                          </div>
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        by {discussion.author}
                      </p>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {discussion.authorRole}
                      </p>
                        </div>
                  </div>
                  
                  {/* Course and Credit Info */}
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
                      {discussion.course}
                    </span>
                    {discussion.credits > 0 && (
                      <span className="px-2 py-1 rounded text-xs font-medium bg-[#AC5757]/20 text-[#AC5757]">
                        +{discussion.credits} XP
                      </span>
                    )}
                  </div>
                  
                  {/* Preview of discussion content */}
                  <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-4 line-clamp-3`}>
                    {discussion.content.substring(0, 200)}...
                  </div>
                  
                  {/* Engagement stats */}
                  <div className={`flex flex-wrap items-center justify-between gap-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <div className="flex items-center gap-4">
                      <button
                        type="button"
                        onClick={(event) => handleDiscussionUpvote(event, idx)}
                        className={`flex items-center gap-1 px-3 py-1 rounded-full border text-xs font-medium transition-colors ${
                          isDarkMode
                            ? 'border-gray-700 text-gray-200 hover:bg-gray-700'
                            : 'border-gray-200 text-gray-600 hover:bg-gray-100'
                        }`}
                        aria-label={`Upvote discussion ${discussion.title}`}
                      >
                        <ThumbsUp size={14} className="text-[#AC5757]" />
                        {discussionUpvotes[idx]}
                      </button>
                      <span>{discussion.replies} replies</span>
                      <span>{discussion.lastActive}</span>
                    </div>
                    <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {discussion.conversation.length} active participants
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Study Groups Tab */}
        {activeTab === 'study-groups' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className={`font-judson text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Study Groups</h2>
              <button 
                onClick={() => alert('Create Study Group\n\nThis would open a form to:\n• Name your study group\n• Set description and goals\n• Choose meeting schedule\n• Invite members')}
                className="bg-[#AC5757] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#8A4A4A] transition-colors"
              >
                Create Group
              </button>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {studyGroups.map((group, idx) => (
                <div 
                  key={idx} 
                  className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow cursor-pointer flex flex-col h-full`}
                  onClick={() => handleGroupClick(group)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} text-lg`}>{group.name}</h3>
                        {group.isProfessorAssigned && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#AC5757]/20 text-[#AC5757]">
                            Professor-Assigned
                          </span>
                        )}
                      </div>
                      <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm mb-3`}>{group.description}</p>
                      <div className={`flex flex-wrap items-center gap-4 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'} mb-3`}>
                        <span><span className="font-medium">Course:</span> {group.course}</span>
                        <span><span className="font-medium">Prof:</span> {group.professor}</span>
                        <span><span className="font-medium">Credits:</span> {group.credits} XP</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2 mt-2">
                    <div className={`flex items-center gap-6 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    <div className="flex items-center gap-1">
                      <Users size={14} className={isDarkMode ? 'text-gray-300' : 'text-gray-700'} />
                        {group.members}/{group.maxMembers} members
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock size={14} className={isDarkMode ? 'text-gray-300' : 'text-gray-700'} />
                        {group.weeklyHours}h/week
                      </div>
                      <div>
                        Next: {group.nextSession}
                    </div>
                  </div>

                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} flex items-center gap-2`}>
                      <MapPin size={12} className={isDarkMode ? 'text-gray-300' : 'text-gray-700'} />
                      <span>{group.meetingLocation}</span>
                    </div>

                    <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {group.requirements}
                    </div>
                  </div>
                  
                  <div className="mt-6 flex justify-end">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleJoinGroup(group.name);
                    }}
                      className="bg-[#AC5757] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#8A4A4A] transition-colors"
                  >
                    Join Group {group.credits > 0 ? `(+${group.credits} XP)` : ''}
                  </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Course Discussions Tab */}
        {activeTab === 'courses' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
            <h2 className={`font-judson text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Course Discussions</h2>
            </div>
            
            <div className="space-y-4">
              {courseDiscussions.map((course, idx) => (
                <div key={idx} className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow cursor-pointer flex flex-col h-full`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} text-lg`}>{course.courseName}</h3>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          {course.credits} Credits
                        </span>
                      </div>
                      
                      <div className="mb-3">
                        <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm mb-1`}>
                          <span className="font-medium">Arabic:</span> {course.instructorArabic}
                        </p>
                        <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>
                          <span className="font-medium">English:</span> {course.instructorEnglish}
                        </p>
                      </div>
                      
                      <div className="space-y-2">
                      <div className={`flex items-center gap-6 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        <div>
                          <span className="font-medium">{course.activeTopics}</span> active topics
                        </div>
                        <div>
                          <span className="font-medium">{course.participants}</span> participants
                        </div>
                        <div>Last post: {course.lastPost}</div>
                        </div>
                        
                        <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-3`}>
                          <span className="font-medium">Last reply:</span> {course.lastReplyBy}
                        </div>
                        
                        {course.hasDueDate && (
                          <div className="flex items-center gap-2 text-sm text-orange-600">
                            <Clock size={14} />
                            <span className="font-medium">Due Date:</span>
                            <span>{new Date(course.dueDate).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end">
                    <button 
                      onClick={(event) => {
                        event.stopPropagation();
                        handleJoinCourseDiscussion(course.courseName);
                      }}
                      className="bg-[#AC5757] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#8A4A4A] transition-colors"
                    >
                      Join Discussion
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
          </div>
        )}
      </div>

      {/* Discussion Detail Modal */}
      {showDiscussionModal && selectedDiscussion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto`}>
            <div className={`sticky top-0 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border-b border-gray-200 p-6 rounded-t-2xl`}>
              <div className="flex justify-between items-start">
                <div className="flex-1 mr-4">
                  <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-2`}>{selectedDiscussion.title}</h2>
                  <div className={`flex items-center gap-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-[#AC5757] rounded-full flex items-center justify-center text-white text-xs font-bold">
                        {selectedDiscussion.authorAvatar}
                      </div>
                    <span>by {selectedDiscussion.author}</span>
                      <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>• {selectedDiscussion.authorRole}</span>
                    </div>
                    <span>•</span>
                    <span>{selectedDiscussion.replies} replies</span>
                    <span>•</span>
                    <span>{selectedDiscussion.lastActive}</span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <span className="text-green-600">▲</span>
                      {selectedDiscussion.upvotes} upvotes
                    </span>
                  </div>
                </div>
                <button 
                  onClick={() => setShowDiscussionModal(false)}
                  className={`${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-6">
                {/* Original Post */}
                <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-6`}>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#AC5757] rounded-full flex items-center justify-center text-white font-bold">
                      {selectedDiscussion.authorAvatar}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{selectedDiscussion.author}</span>
                        <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{selectedDiscussion.authorRole}</span>
                        <span className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>• {selectedDiscussion.lastActive}</span>
                      </div>
                      <div className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} whitespace-pre-line`}>
                        {selectedDiscussion.content}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Conversation Thread */}
                <div className="space-y-4">
                  <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Replies ({selectedDiscussion.conversation.length})</h3>
                  {selectedDiscussion.conversation.map((reply, idx) => (
                    <div key={idx} className="border-l-2 border-gray-200 pl-4">
                      <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-white'} rounded-lg p-4 border border-gray-100`}>
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                            {reply.authorAvatar}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{reply.author}</span>
                              <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{reply.authorRole}</span>
                              <span className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>• {reply.timestamp}</span>
                              <span className={`flex items-center gap-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                <span className="text-green-600">▲</span>
                                {reply.upvotes}
                              </span>
                            </div>
                            <div className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>
                              {reply.content}
                            </div>
                            
                            {/* Nested Replies */}
                            {reply.replies && reply.replies.length > 0 && (
                              <div className="ml-4 space-y-3">
                                {reply.replies.map((nestedReply, nestedIdx) => (
                                  <div key={nestedIdx} className={`${isDarkMode ? 'bg-gray-600' : 'bg-gray-50'} rounded-lg p-3`}>
                                    <div className="flex items-start gap-2">
                                      <div className="w-6 h-6 bg-[#AC5757] rounded-full flex items-center justify-center text-white text-xs font-bold">
                                        {nestedReply.authorAvatar}
                                      </div>
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} text-sm`}>{nestedReply.author}</span>
                                          <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{nestedReply.authorRole}</span>
                                          <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>• {nestedReply.timestamp}</span>
                                          <span className={`flex items-center gap-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                            <span className="text-green-600">▲</span>
                                            {nestedReply.upvotes}
                                          </span>
                                        </div>
                                        <div className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} text-sm`}>
                                          {nestedReply.content}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button 
                    onClick={() => {
                      alert('Reply added!\n\nYour response has been posted to the discussion.');
                      setShowDiscussionModal(false);
                    }}
                    className="flex-1 bg-[#AC5757] text-white py-3 px-6 rounded-lg font-semibold hover:bg-[#8A4A4A] transition-colors"
                  >
                    Reply to Discussion
                  </button>
                  <button 
                    onClick={() => alert('Discussion bookmarked!\n\nYou can find it in your saved items.')}
                    className={`px-6 py-3 border ${isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} rounded-lg transition-colors`}
                  >
                    Bookmark
                  </button>
                  <button 
                    onClick={() => alert('Discussion upvoted!\n\nThanks for your contribution.')}
                    className={`px-6 py-3 border ${isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} rounded-lg transition-colors flex items-center gap-2`}
                  >
                    <span className="text-green-600">▲</span>
                    Upvote
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Study Group Detail Modal */}
      {showGroupModal && selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl max-w-lg w-full`}>
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{selectedGroup.name}</h2>
                    {selectedGroup.isProfessorAssigned && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#AC5757] text-white">
                        Professor-Assigned
                      </span>
                    )}
                  </div>
                  <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {selectedGroup.course} • {selectedGroup.professor}
                  </div>
                </div>
                <button 
                  onClick={() => setShowGroupModal(false)}
                  className={`${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  ✕
                </button>
              </div>
              
              <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-4`}>{selectedGroup.description}</p>
              
              {/* Credit System */}
              <div className="bg-gradient-to-r from-[#AC5757]/10 to-[#AC5757]/5 rounded-lg p-4 mb-4 border border-[#AC5757]/20">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Credits Available</span>
                  <span className="text-2xl font-bold text-[#AC5757]">{selectedGroup.credits} XP</span>
                </div>
                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Earn credits by participating in study sessions, completing assignments, and contributing to group discussions
                </div>
              </div>
              
              <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-4 mb-4`}>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center justify-between">
                  <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Members:</span>
                    <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>{selectedGroup.members}/{selectedGroup.maxMembers}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Weekly Hours:</span>
                    <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>{selectedGroup.weeklyHours}h</span>
                </div>
                  <div className="flex items-center justify-between">
                  <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Next Session:</span>
                  <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>{selectedGroup.nextSession}</span>
                </div>
                  <div className="flex items-center justify-between">
                  <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Category:</span>
                  <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>{selectedGroup.category}</span>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>
                    <MapPin size={14} />
                    {selectedGroup.meetingLocation}
                  </div>
                  <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                    Requirements: {selectedGroup.requirements}
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <button 
                  onClick={() => handleJoinGroup(selectedGroup.name)}
                  className="w-full bg-[#AC5757] text-white py-3 px-6 rounded-lg font-semibold hover:bg-[#8A4A4A] transition-colors"
                >
                  Join Group {selectedGroup.credits > 0 ? `(+${selectedGroup.credits} XP)` : ''}
                </button>
                <button 
                  onClick={() => setShowGroupModal(false)}
                  className={`w-full border ${isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} py-3 px-6 rounded-lg transition-colors`}
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Notification Popup */}
      {showNotification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl max-w-md w-full p-6 shadow-2xl`}>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-3`}>Success!</h3>
              <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-6 whitespace-pre-line`}>
                {notificationMessage}
              </p>
              <button
                onClick={() => setShowNotification(false)}
                className="bg-[#AC5757] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#8A4A4A] transition-colors"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Course Discussion Notification Popup */}
      {showCourseNotification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl max-w-md w-full p-6 shadow-2xl`}>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-3`}>Discussion Joined!</h3>
              <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-6 whitespace-pre-line`}>
                {courseNotificationMessage}
              </p>
              <button
                onClick={() => setShowCourseNotification(false)}
                className="bg-[#AC5757] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#8A4A4A] transition-colors"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Discussion Modal */}
      {showNewDiscussionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl max-w-lg w-full shadow-2xl`}>
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Create New Discussion</h2>
                <button 
                  onClick={() => setShowNewDiscussionModal(false)}
                  className={`${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  ✕
                </button>
              </div>

              <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-6 mb-6`}>
                <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  This would open a form where you can:
                </p>
                <ul className={`space-y-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <li className="flex items-start gap-2">
                    <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>•</span>
                    <span>Choose a topic category</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>•</span>
                    <span>Write your question or topic</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>•</span>
                    <span>Add tags for better discovery</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>•</span>
                    <span>Attach images or files</span>
                  </li>
                </ul>
              </div>

              <div className="flex gap-3">
                <button 
                  onClick={() => {
                    alert('Discussion form would open here in a real application!');
                    setShowNewDiscussionModal(false);
                  }}
                  className="flex-1 bg-[#AC5757] text-white py-3 px-6 rounded-lg font-semibold hover:bg-[#8A4A4A] transition-colors"
                >
                  Get Started
                </button>
                <button 
                  onClick={() => setShowNewDiscussionModal(false)}
                  className={`px-6 py-3 border ${isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} rounded-lg transition-colors`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Community;